import {
  Module,
  VuexModule,
  Action,
  Mutation,
  getModule
} from 'vuex-module-decorators'
import Vue from 'vue'
import axios from 'axios'

import store from '@/store'
import eventModule from '@/store/event'
import userModule from '@/store/user'
import { Deal, Bookmark } from '@/types/Deal'
import { DiscountType, DealType } from '@/utils/enums'
import { isEmpty } from 'lodash'

const name = 'deal'

interface FilterData {
  name: string
  image: string | null
}

export interface FilterDataSingle extends FilterData {
  _id: string
}

export interface FilterDataMulti extends FilterData {
  ids: string[]
}

@Module({
  namespaced: true,
  dynamic: true,
  name,
  store
})
class DealModule extends VuexModule {
  private currentDeal!: Deal
  private allDeals!: Record<string, Deal[]>
  private allRewards!: Deal[]
  private allRedemptions!: Deal[]
  private currentType = localStorage.selectedDealType || DealType.LOCAL
  private currentBookmarks!: Bookmark[]

  get deal(): Deal {
    return this.currentDeal
  }

  get deals(): Deal[] {
    return Object.values(this.allDeals).reduce((cum, cur) => {
      cum.push(...cur)
      return cum
    }, [])
  }

  get interestingDeals(): Deal[] {
    return this.allDeals?.interestDeals || []
  }

  get otherDeals(): Deal[] {
    return this.allDeals?.otherDeals || []
  }

  get nearbyDeals(): Deal[] {
    return this.allDeals?.nearbyDeals || []
  }

  get rewards(): Deal[] {
    return this.allRewards
  }

  get redemptions(): Deal[] {
    return this.allRedemptions
  }

  get type(): string {
    return this.currentType
  }

  get bookmarks(): Bookmark[] {
    return this.currentBookmarks
  }

  get isLocal(): boolean {
    return this.deal?.type === DealType.LOCAL || false
  }

  get isEcommerce(): boolean {
    return this.deal?.type === DealType.ECOMMERCE || false
  }

  get isReward(): boolean {
    return this.deal?.type === DealType.REWARDS || false
  }

  get isRelief(): boolean {
    return this.deal?.discount.type === DiscountType.RELIEF || false
  }

  get isDiscount(): boolean {
    return this.deal?.discount.type === DiscountType.DISCOUNT || false
  }

  get isPercentage(): boolean {
    return this.deal?.discount.type === DiscountType.PERCENTAGE || false
  }

  get isFree(): boolean {
    return this.deal?.discount.type === DiscountType.FREEITEMS || false
  }

  get isGoodie(): boolean {
    return this.deal?.discount.type === DiscountType.GOODIES || false
  }

  get isCoupon(): boolean {
    return this.deal?.discount.type === DiscountType.COUPON || false
  }

  get isInfo(): boolean {
    return this.deal?.discount.type === DiscountType.INFO || false
  }

  get possibleFilters() {
    let collectedCategories: Record<string, FilterDataSingle> = {}
    let collectedCities: Record<string, FilterDataMulti> = {}
    let collectedTags: Record<string, FilterDataSingle> = {}

    // let collectedCities: Record<any, string> = {}
    for (const deal of this.deals) {
      collectedCategories = deal.categories.reduce(
        (cum, { _id, name, image }) => ({
          ...cum,
          [name]: { _id, name, image }
        }),
        collectedCategories
      )

      collectedCities = deal.venues.reduce((cum, { _id, address }) => {
        if (!cum[address.city])
          cum[address.city] = { ids: [], name: address.city, image: null }
        cum[address.city].ids.push(_id)

        return cum
      }, collectedCities)

      collectedTags = deal.tags.reduce(
        (cum, { _id, name, image }) => ({
          ...cum,
          [name]: { _id, name, image }
        }),
        collectedTags
      )
    }

    return {
      categories: collectedCategories,
      venues: collectedCities,
      tags: collectedTags
    }
  }

  @Mutation
  private setCurrentDeal(payload: Deal) {
    Vue.set(this, 'currentDeal', payload)
  }

  @Mutation
  private setAllDeals(payload: Deal[]) {
    Vue.set(this, 'allDeals', payload)
  }
  @Mutation
  private setAllRewards(payload: Deal) {
    Vue.set(this, 'allRewards', payload)
  }
  @Mutation
  private setAllRedemptions(payload: Deal) {
    Vue.set(this, 'allRedemptions', payload)
  }

  @Mutation
  private setCurrentType(payload: string) {
    Vue.set(this, 'currentType', payload)
  }

  @Mutation
  private setBookmarks(payload: Bookmark[]) {
    Vue.set(this, 'currentBookmarks', payload)
  }

  @Action({ rawError: true })
  async fetch(id: string) {
    try {
      const { data } = await axios.get(`/deals/${id}`)
      await this.context.commit('setCurrentDeal', data.deal)
    } catch (error) {
      eventModule.newError('Deal konnte nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async fetchBookmarks() {
    try {
      // Bookmarks leer setzen um doppelten Abruf abzufangen.
      this.context.commit('setBookmarks', [])
      const { data } = await axios.get(`/bookmarks`)
      await this.context.commit('setBookmarks', data.bookmarks)
    } catch (error) {
      eventModule.newError('Favoriten konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async createBookmark(deal: Deal) {
    try {
      await axios.post(`/bookmarks`, { dealId: deal._id })
      eventModule.newMessage('Deal wurde deinen Favoriten hinzugefügt')
    } catch (error) {
      eventModule.newError('Favorit konnte nicht erstellt werden')
    }
  }

  @Action({ rawError: true })
  async deleteBookmark(deal: Deal) {
    try {
      await axios.delete(`/bookmarks/${deal._id}`)
      eventModule.newMessage('Deal wurde aus deinen Favoriten gelöscht')
    } catch (error) {
      eventModule.newError('Favorit konnte nicht gelöscht werden')
    }
  }

  @Action({ rawError: true })
  async fetchAll() {
    this.context.commit('setAllDeals', [])

    try {
      if (this.type === DealType.LOCAL) {
        if (!navigator.geolocation) {
          eventModule.newInfo(
            'Dein Browser unterstützt keine GPS-Lokalisierung, daher sind keine lokalen Deals vorhanden'
          )
        } else {
          navigator.geolocation.getCurrentPosition(
            async position => {
              userModule.setLocation({
                lat: position.coords.latitude,
                lng: position.coords.longitude
              })
              const { data } = await axios.get(
                `/deals${eventModule.searchTerm ? '/search' : ''}`,
                {
                  params: {
                    q: eventModule.searchTerm
                      ? eventModule.searchTerm
                      : undefined,
                    type: this.type,
                    location: eventModule.searchTerm
                      ? undefined
                      : `${position.coords.latitude};${position.coords.longitude}`
                  }
                }
              )
              this.context.commit('setAllDeals', data)

              if (isEmpty(data)) {
                eventModule.newInfo(
                  'Es sind momentan keine lokalen Deals für deinen Standort vorhanden'
                )
              }
            },
            async () => {
              const { data } = await axios.get(
                `/deals${eventModule.searchTerm ? '/search' : ''}`,
                {
                  params: {
                    q: eventModule.searchTerm
                      ? eventModule.searchTerm
                      : undefined,
                    type: this.type
                  }
                }
              )
              this.context.commit('setAllDeals', data)
              if (isEmpty(data)) {
                eventModule.newInfo(
                  'Es sind momentan keine lokalen Deals vorhanden'
                )
              }
            }
          )
        }
      } else {
        const { data } = await axios.get(
          `/deals${eventModule.searchTerm.length ? '/search' : ''}`,
          {
            params: {
              q: eventModule.searchTerm ? eventModule.searchTerm : undefined,
              type: this.type
            }
          }
        )

        this.context.commit('setAllDeals', data)
      }
    } catch (error) {
      eventModule.newError('Deals konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async fetchRewards() {
    try {
      const { data } = await axios.get(`/deals`, {
        params: {
          type: DealType.REWARDS
        }
      })
      await this.context.commit('setAllRewards', data.deals)
    } catch (error) {
      eventModule.newError('Rewards-Deals konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async fetchRedemptions() {
    try {
      const { data } = await axios.get(`/deals/redemptions/options`)
      await this.context.commit('setAllRedemptions', data.deals)
    } catch (error) {
      eventModule.newError('Eingelöste Deals konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async redeem(code: string) {
    try {
      await axios.post(`/deals/${this.deal._id}/redeem`, { code })
      eventModule.newMessage('Deal wurde erfolgreich eingelöst')
    } catch (error) {
      eventModule.newError('Deal konnte nicht eingelöst werden')
    }
  }

  @Action({ rawError: true })
  async createRedemption(payload: { orderNumber: string; deal: string }) {
    try {
      await axios.post(`/deals/redemptions`, payload)
      eventModule.newMessage('Deal wurde erfolgreich eingelöst')
    } catch (error) {
      eventModule.newError('Deal konnte nicht eingelöst werden')
    }
  }

  @Action({ rawError: true })
  async fetchCode() {
    try {
      const { data } = await axios.get(`/deals/${this.deal._id}/code`)
      return data.code
    } catch (error) {
      eventModule.newError('Code konnte nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  getActiveCouponPrice(deal: Deal): number {
    if (!Array.isArray(deal.discount.couponValue)) return 0
    const coupon = deal.discount.couponValue?.find(el => el.active === true)
    if (coupon) {
      return coupon.couponPrice
    } else {
      return 0
    }
  }

  @Action({ rawError: true })
  getActiveRetailPrice(deal: Deal): number {
    if (!Array.isArray(deal.discount.couponValue)) return 0
    const coupon = deal.discount.couponValue?.find(el => el.active === true)
    if (coupon) {
      return coupon.retailPrice
    } else {
      return 0
    }
  }

  @Action({ rawError: true })
  setDeal(payload: Deal) {
    this.context.commit('setCurrentDeal', payload)
  }

  @Action({ rawError: true })
  setDeals(payload: Deal[]) {
    this.context.commit('setAllDeals', payload)
  }

  @Action({ rawError: true })
  setType(type: string) {
    this.context.commit('setCurrentType', type)
    localStorage.selectedDealType = type
  }
}

export default getModule(DealModule)
